/**
 * SYNTHETIC TEST DATA ONLY
 *
 * Preview calendar fixtures. Sequential ids. No dates of birth, no MRNs, no
 * live clinic rows. The "Nikki" patient is a named seed the walkthrough asked
 * for, not a copied chart.
 */

export const APPOINTMENT_STORAGE_KEY = 'dubbed.staff.app.appointments';
export const CALENDAR_MODE_KEY = 'dubbed.staff.app.calendarMode';
export const CALENDAR_DATE_KEY = 'dubbed.staff.app.calendarDate';

export const DAY_START_MINUTES = 8 * 60;
export const DAY_END_MINUTES = 18 * 60;
export const SLOT_MINUTES = 30;

export type CalendarMode = 'day' | 'week';

export interface SeedPatient {
  readonly id: string;
  readonly displayName: string;
  readonly code: string;
}

export interface SeedService {
  readonly id: string;
  readonly name: string;
  readonly durationMinutes: number;
  readonly priceCents: number;
  readonly cardOnFileRequired: boolean;
}

export interface SeedProvider {
  readonly id: string;
  readonly displayName: string;
  readonly locationIds: readonly string[];
}

export interface Appointment {
  readonly id: string;
  readonly locationId: string;
  readonly patientId: string;
  readonly serviceId: string;
  readonly providerId: string;
  readonly startMs: number;
  readonly endMs: number;
  readonly notes: string;
  readonly cardRequired: boolean;
  readonly cardBypassed: boolean;
}

export interface BookAppointmentInput {
  readonly locationId: string;
  readonly patientId: string;
  readonly serviceId: string;
  readonly providerId: string;
  readonly startMs: number;
  readonly cardRequired: boolean;
  readonly cardBypassed: boolean;
  readonly notes?: string;
}

export const SEED_PATIENTS: readonly SeedPatient[] = [
  { id: 'patient-000010', displayName: 'Nikki Preview', code: 'PAT-000010' },
  { id: 'patient-000011', displayName: 'Testpatient Alpha', code: 'PAT-000011' },
  { id: 'patient-000012', displayName: 'Testpatient Bravo', code: 'PAT-000012' },
];

export const SEED_SERVICES: readonly SeedService[] = [
  {
    id: 'service-000010',
    name: 'General Consultation - 20 Min',
    durationMinutes: 20,
    priceCents: 5000,
    cardOnFileRequired: true,
  },
  {
    id: 'service-000011',
    name: 'Follow-up Visit - 30 Min',
    durationMinutes: 30,
    priceCents: 7500,
    cardOnFileRequired: false,
  },
];

export const SEED_PROVIDERS: readonly SeedProvider[] = [
  {
    id: 'provider-000010',
    displayName: 'Tracey Schaper',
    locationIds: ['location-000010', 'location-000011'],
  },
  {
    id: 'provider-000011',
    displayName: 'Bianca Capella',
    locationIds: ['location-000010', 'location-000012'],
  },
];

export const NIKKI_PATIENT_ID = 'patient-000010';
export const CONSULTATION_SERVICE_ID = 'service-000010';
export const TRACEY_PROVIDER_ID = 'provider-000010';

export function searchPatients(query: string): readonly SeedPatient[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return SEED_PATIENTS;
  }
  return SEED_PATIENTS.filter(
    (patient) =>
      patient.displayName.toLowerCase().includes(needle) ||
      patient.code.toLowerCase().includes(needle),
  );
}

export function findPatient(id: string): SeedPatient | undefined {
  return SEED_PATIENTS.find((patient) => patient.id === id);
}

export function findService(id: string): SeedService | undefined {
  return SEED_SERVICES.find((service) => service.id === id);
}

export function findProvider(id: string): SeedProvider | undefined {
  return SEED_PROVIDERS.find((provider) => provider.id === id);
}

export function providersForLocation(locationId: string): readonly SeedProvider[] {
  return SEED_PROVIDERS.filter((provider) => provider.locationIds.includes(locationId));
}

export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  const weekday = day.getDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  return addDays(day, mondayOffset);
}

export function sameDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

export function dateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function parseDateKey(key: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

export function minutesSinceMidnight(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function atMinutes(day: Date, minutes: number): Date {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, mins, 0, 0);
}

export function slotStarts(): readonly number[] {
  const slots: number[] = [];
  for (let minutes = DAY_START_MINUTES; minutes < DAY_END_MINUTES; minutes += SLOT_MINUTES) {
    slots.push(minutes);
  }
  return slots;
}

export function formatClock(minutes: number): string {
  const hours24 = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const suffix = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return mins === 0 ? `${hours12} ${suffix}` : `${hours12}:${String(mins).padStart(2, '0')} ${suffix}`;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function formatDayHeading(date: Date): string {
  return `${WEEKDAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export function formatWeekHeading(date: Date): string {
  const start = startOfWeek(date);
  const end = addDays(start, 6);
  return `${MONTHS[start.getMonth()]} ${start.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
}

export function formatTimeRange(startMs: number, endMs: number): string {
  return `${formatClock(minutesSinceMidnight(new Date(startMs)))} – ${formatClock(minutesSinceMidnight(new Date(endMs)))}`;
}

export function appointmentsForLocationOnDay(
  appointments: readonly Appointment[],
  locationId: string,
  day: Date,
): readonly Appointment[] {
  return appointments.filter(
    (appointment) => appointment.locationId === locationId && sameDay(new Date(appointment.startMs), day),
  );
}

export function appointmentsInWeek(
  appointments: readonly Appointment[],
  locationId: string,
  weekStart: Date,
): readonly Appointment[] {
  const end = addDays(weekStart, 7);
  return appointments.filter((appointment) => {
    if (appointment.locationId !== locationId) {
      return false;
    }
    const start = new Date(appointment.startMs);
    return start >= weekStart && start < end;
  });
}

export function canBookAppointment(
  input: BookAppointmentInput,
): { ok: true } | { ok: false; reason: string } {
  if (!findPatient(input.patientId)) {
    return { ok: false, reason: 'Select a seed patient.' };
  }
  const service = findService(input.serviceId);
  if (!service) {
    return { ok: false, reason: 'Select a service.' };
  }
  const provider = findProvider(input.providerId);
  if (!provider) {
    return { ok: false, reason: 'Select a provider.' };
  }
  if (!provider.locationIds.includes(input.locationId)) {
    return { ok: false, reason: 'That provider is not assigned to this location.' };
  }
  if (input.cardRequired && !input.cardBypassed) {
    return { ok: false, reason: 'Card on file required. Check Bypass card requirement to book in preview.' };
  }
  if (!Number.isFinite(input.startMs)) {
    return { ok: false, reason: 'Pick a start time.' };
  }
  return { ok: true };
}

export function nextAppointmentId(existing: readonly Appointment[]): string {
  let max = 0;
  for (const appointment of existing) {
    const match = /^appointment-(\d+)$/.exec(appointment.id);
    if (match) {
      max = Math.max(max, Number(match[1]));
    }
  }
  return `appointment-${String(max + 1).padStart(6, '0')}`;
}

export function buildSeedAppointments(now: Date): Appointment[] {
  const day = startOfDay(now);
  const consult = findService(CONSULTATION_SERVICE_ID);
  const duration = consult?.durationMinutes ?? 20;
  const nikkiStart = atMinutes(day, 10 * 60);
  const barwisStart = atMinutes(day, 11 * 60);
  return [
    {
      id: 'appointment-000010',
      locationId: 'location-000010',
      patientId: NIKKI_PATIENT_ID,
      serviceId: CONSULTATION_SERVICE_ID,
      providerId: TRACEY_PROVIDER_ID,
      startMs: nikkiStart.getTime(),
      endMs: nikkiStart.getTime() + duration * 60_000,
      notes: '',
      cardRequired: true,
      cardBypassed: true,
    },
    {
      id: 'appointment-000011',
      locationId: 'location-000011',
      patientId: 'patient-000011',
      serviceId: CONSULTATION_SERVICE_ID,
      providerId: TRACEY_PROVIDER_ID,
      startMs: barwisStart.getTime(),
      endMs: barwisStart.getTime() + duration * 60_000,
      notes: '',
      cardRequired: true,
      cardBypassed: true,
    },
  ];
}

function parseAppointments(raw: string): Appointment[] | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return null;
    }
    const rows: Appointment[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== 'object') {
        return null;
      }
      const row = item as Record<string, unknown>;
      if (
        typeof row.id !== 'string' ||
        typeof row.locationId !== 'string' ||
        typeof row.patientId !== 'string' ||
        typeof row.serviceId !== 'string' ||
        typeof row.providerId !== 'string' ||
        typeof row.startMs !== 'number' ||
        typeof row.endMs !== 'number' ||
        typeof row.notes !== 'string' ||
        typeof row.cardRequired !== 'boolean' ||
        typeof row.cardBypassed !== 'boolean'
      ) {
        return null;
      }
      rows.push({
        id: row.id,
        locationId: row.locationId,
        patientId: row.patientId,
        serviceId: row.serviceId,
        providerId: row.providerId,
        startMs: row.startMs,
        endMs: row.endMs,
        notes: row.notes,
        cardRequired: row.cardRequired,
        cardBypassed: row.cardBypassed,
      });
    }
    return rows;
  } catch {
    return null;
  }
}

export function loadAppointments(storage: Storage, now: Date = new Date()): Appointment[] {
  const raw = storage.getItem(APPOINTMENT_STORAGE_KEY);
  if (raw === null) {
    const seed = buildSeedAppointments(now);
    storage.setItem(APPOINTMENT_STORAGE_KEY, JSON.stringify(seed));
    return seed;
  }
  return parseAppointments(raw) ?? [];
}

export function saveAppointments(appointments: readonly Appointment[], storage: Storage): void {
  storage.setItem(APPOINTMENT_STORAGE_KEY, JSON.stringify(appointments));
}

export function bookAppointment(
  input: BookAppointmentInput,
  storage: Storage,
  now: Date = new Date(),
): Appointment {
  const gate = canBookAppointment(input);
  if (!gate.ok) {
    throw new Error(gate.reason);
  }
  const service = findService(input.serviceId);
  if (!service) {
    throw new Error('Select a service.');
  }
  const existing = loadAppointments(storage, now);
  const created: Appointment = {
    id: nextAppointmentId(existing),
    locationId: input.locationId,
    patientId: input.patientId,
    serviceId: input.serviceId,
    providerId: input.providerId,
    startMs: input.startMs,
    endMs: input.startMs + service.durationMinutes * 60_000,
    notes: input.notes ?? '',
    cardRequired: input.cardRequired,
    cardBypassed: input.cardBypassed,
  };
  saveAppointments([...existing, created], storage);
  return created;
}

export function updateAppointmentNotes(
  appointmentId: string,
  notes: string,
  storage: Storage,
  now: Date = new Date(),
): Appointment | null {
  const existing = loadAppointments(storage, now);
  const index = existing.findIndex((appointment) => appointment.id === appointmentId);
  const current = existing[index];
  if (!current) {
    return null;
  }
  const next = { ...current, notes };
  const copy = [...existing];
  copy[index] = next;
  saveAppointments(copy, storage);
  return next;
}

export function readCalendarMode(storage: Storage): CalendarMode {
  return storage.getItem(CALENDAR_MODE_KEY) === 'week' ? 'week' : 'day';
}

export function writeCalendarMode(mode: CalendarMode, storage: Storage): void {
  storage.setItem(CALENDAR_MODE_KEY, mode);
}

export function readCalendarDate(storage: Storage, fallback: Date = new Date()): Date {
  const stored = storage.getItem(CALENDAR_DATE_KEY);
  return (stored ? parseDateKey(stored) : null) ?? startOfDay(fallback);
}

export function writeCalendarDate(date: Date, storage: Storage): void {
  storage.setItem(CALENDAR_DATE_KEY, dateKey(date));
}
